import {
  Alert,
  Button,
  Card,
  CardHeader,
  CardBody,
  Form,
  Input,
  Link,
} from '@heroui/react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, FormEvent, useState } from 'react';
import { useParams } from 'react-router';

import useHospitalFlags from '@/hooks/useHospitalFlags';
import {
  useRequestPasswordMutation,
  useResetPasswordMutation,
} from '@/patient-app-common/api/authApi';

import { FormFieldSecure } from '../layout/Elements';

import { ReCaptcha } from './layout/ReCaptcha';

const MAIL_TO =
  'mailto:support@seamless.md?subject=SeamlessMD%20Support%20request%20-%20Password%20Reset&body=Please%20include%20the%20following%20information%3A%0A%0APatient%20Name%3A%0AHospital%20Name%3A%0AEmail%3A%0APhone%20number%3A';

export function PasswordResetForm() {
  const { token } = useParams<{ token: string }>();

  if (token) {
    return <ResetPassword />;
  }

  return <RequestPassword />;
}

export function RequestPassword() {
  const { t } = useTranslation('login');

  const [requestPassword, { isLoading }] = useRequestPasswordMutation();

  const [contact, setContact] = useState('');
  const [isHuman, setIsHuman] = useState(true);
  const [error, setError] = useState('');
  const [serverError, setServerError] = useState('');
  const [success, setSuccess] = useState(false);

  const validateInput = () => {
    const cleanedInput = contact.replace(/[()\-\s]/g, '');
    const isPhone = cleanedInput && !_.isNaN(_.toNumber(cleanedInput));

    let valid = null;

    if (isPhone) {
      valid = cleanedInput.match(/^\d{10}$/);
    } else {
      const emailRegex =
        // eslint-disable-next-line no-useless-escape
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      valid = contact.match(emailRegex);
    }

    if (!valid) {
      setError(t('requestpassword.error.invalid_contact'));
    }

    return valid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!contact) {
      setError(t('common.error.blank_input'));
      return;
    }

    const valid = validateInput();

    if (!valid) return;

    if (!isHuman) {
      setError(t('common.error.recaptcha'));
      return;
    }

    setError('');
    setServerError('');

    try {
      await requestPassword({ email: contact }).unwrap();
      setSuccess(true);
    } catch (e) {
      console.error(e);
      setServerError(t('common.error.server'));
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-[100dvh]">
      <div className="sm:max-w-xl w-full">
        <Card shadow="sm" className="border border-grey px-4">
          <CardHeader>
            <h1 className="text-xl font-semibold">
              {t('common.link.help_account')}
            </h1>
          </CardHeader>
          <CardBody className="gap-4">
            <p>{t('requestpassword.description')}</p>
            <p>{t('requestpassword.help_text')}</p>
            <Link href={MAIL_TO} isExternal>
              {t('common.link.help')}
            </Link>
            <Alert
              color="danger"
              hideIcon
              isVisible={!_.isEmpty(serverError)}
              radius="none"
              title={serverError}
            />
            <Alert
              color="success"
              hideIcon
              isVisible={success}
              radius="none"
              title={t('requestpassword.response_success')}
            />
            <Form onSubmit={handleSubmit}>
              <Input
                autoComplete="off"
                color="primary"
                className="mb-4"
                errorMessage={error}
                isInvalid={!_.isEmpty(error)}
                label={t('requestpassword.input')}
                name="email"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setError('');
                  setSuccess(false);
                  setContact(e.target.value);
                }}
                value={contact}
                variant="bordered"
              />
              <ReCaptcha setIsHuman={(result: boolean) => setIsHuman(result)} />
              <Button
                className="mx-auto w-1/2"
                color="primary"
                isLoading={isLoading}
                type="submit"
              >
                {t('requestpassword.button.submit')}
              </Button>
            </Form>
            <div className="flex flex-col items-center">
              <Link className="text-sm" href="/">
                {t('common.link.back_to_login')}
              </Link>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const { t } = useTranslation('login');
  const { requirePasswordLength13 } = useHospitalFlags();
  const { token } = useParams<{ token: string }>();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const [isHuman, setIsHuman] = useState(true);
  const [error, setError] = useState('');
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [serverError, setServerError] = useState('');
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    new: '',
    confirm: '',
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setErrorFields([]);
    setSuccess(false);

    const { name, value } = e.target;
    setForm((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    for (const [key, value] of Object.entries(form)) {
      if (!value) {
        errorFields.push(key);
      }
    }

    const hasNumber = /[0-9]/.test(form.new);
    const requiredLength = requirePasswordLength13 ? 13 : 8;
    const lengthError = `common.error.invalid_password_${requiredLength}`;

    if (form.new && (form.new.length < requiredLength || !hasNumber)) {
      setError(t(lengthError));
      setErrorFields(['new']);
      return false;
    }

    if (errorFields.length && (!form.new || !form.confirm)) {
      setErrorFields(errorFields);
      setError(t('common.error.blank_input'));
      return false;
    }

    if (form.new !== form.confirm) {
      setErrorFields(['confirm']);
      setError(t('common.error.confirm_password'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setErrorFields([]);
    setError('');
    setSuccess(false);

    const valid = validateForm();

    if (!valid) return;

    if (!isHuman) {
      setError(t('common.error.recaptcha'));
      return;
    }

    setError('');

    const params = {
      token,
      password: form.new,
    };

    try {
      const result = await resetPassword(params).unwrap();

      if (result.success) {
        setSuccess(true);
      } else {
        setServerError(t('common.error.server'));
      }
    } catch (e) {
      console.error(e);
      setServerError(t('common.error.server'));
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-[100dvh]">
      <div className="sm:max-w-xl w-full">
        <Card shadow="sm" className="border border-grey px-4">
          <CardHeader>
            <h1 className="text-xl font-semibold">
              {t('resetpassword.title')}
            </h1>
          </CardHeader>
          <CardBody className="gap-4">
            <p>{t('passwordresetform.description_web')}</p>
            <Alert
              color="danger"
              hideIcon
              isVisible={!_.isEmpty(serverError)}
              radius="none"
              title={serverError}
            />
            <Alert
              color="success"
              hideIcon
              isVisible={success}
              radius="none"
              title={t('common.text.update_success')}
            />
            <Form onSubmit={handleSubmit} className="gap-4">
              <FormFieldSecure
                color="primary"
                errorMessage={error}
                isInvalid={errorFields.includes('new')}
                label={t('common.input.password')}
                name="new"
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(e)
                }
                value={form.new}
                variant="bordered"
              />
              <FormFieldSecure
                color="primary"
                errorMessage={error}
                isInvalid={errorFields.includes('confirm')}
                label={t('common.input.confirm_password')}
                name="confirm"
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(e)
                }
                value={form.confirm}
                variant="bordered"
              />
              <ReCaptcha setIsHuman={(result: boolean) => setIsHuman(result)} />
              <Button
                className="mx-auto w-1/2"
                color="primary"
                isDisabled={success}
                isLoading={isLoading}
                type="submit"
              >
                {t('common.action.submit')}
              </Button>
            </Form>
            <div className="flex flex-col items-center">
              <Link className="text-sm" href="/">
                {t('common.link.back_to_login')}
              </Link>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
